import React, { useCallback, useEffect, useState } from 'react'
import { Divider, Form, FormInstance, Input } from 'antd'
import { useTranslation } from 'react-i18next'
import { TextAreaRef } from 'antd/lib/input/TextArea'
import ContextAwareFunctionInformation from './ContextAwareFunctionInformation'
import { FormulaFunction } from '../../../pages/settings/company/keyFigures/formulas/types'
import { Context, ContextRules, contextAnalysis } from '../utils/contextAnalysis'

type FormulaSourceProps = {
  textAreaRef: TextAreaRef | null
  cursor?: { selectionStart: number; selectionEnd: number; select: (pos: number) => void } | null
  form: FormInstance
  functions: Record<string, FormulaFunction>
  setTextAreaRef: (node: TextAreaRef | null) => void
  contextRules?: ContextRules // Optional custom context rules
}

const FormulaSource = ({ textAreaRef, cursor, form, functions, setTextAreaRef, contextRules }: FormulaSourceProps) => {
  const { t } = useTranslation()
  const [contextInfo, setContextInfo] = useState<Context>({ company: 'default', dimension: 'default', path: [] })

  const saveTextAreaRef = useCallback(
    (node: TextAreaRef) => {
      if (node !== null && textAreaRef === null) {
        setTextAreaRef(node)
      }
    },
    [textAreaRef]
  )

  const addArgument = (argument: string) => {
    if (!cursor) return

    const source = form.getFieldValue('source') || ''
    form.setFieldsValue({
      source: `${source.slice(0, cursor.selectionStart)}${argument}${source.slice(cursor.selectionEnd)}`
    })
    setTimeout(() => {
      cursor.select(cursor.selectionStart + argument.length)
    }, 10)
  }

  const handleCursorChange = useCallback(() => {
    const source = form.getFieldValue('source') || ''
    const node = (textAreaRef as any)?.resizableTextArea?.textArea as HTMLTextAreaElement | undefined
    const posFromRef = typeof node?.selectionStart === 'number' ? node.selectionStart : 0
    const cursorPos = typeof cursor?.selectionStart === 'number' ? cursor.selectionStart : posFromRef

    // New context analysis
    const calls = contextAnalysis.detectFunctionCalls(source)

    const context = contextAnalysis.analyzeContext(
      calls,
      cursorPos,
      source,
      contextRules || contextAnalysis.defaultContextRules
    )

    // Include current function state in context using new methods
    const currentFunction = contextAnalysis.getCurrentFunction(source, cursorPos, functions)
    const currentArgument = contextAnalysis.getCurrentArgument(source, cursorPos, currentFunction)
    const prevArgumentValue = contextAnalysis.getPrevArgumentValue(source, cursorPos)

    const enhancedContext = {
      ...context,
      currentFunction,
      currentArgument,
      prevArgumentValue
    }

    setContextInfo(enhancedContext)
  }, [form, cursor, functions, contextRules, textAreaRef])

  // Update context analysis when source or cursor changes
  useEffect(() => {
    handleCursorChange()
  }, [handleCursorChange])

  // Also trigger update when typing (onChange event)
  const handleSourceChange = useCallback(() => {
    // Small delay to ensure form value is updated
    setTimeout(() => {
      handleCursorChange()
    }, 10)
  }, [handleCursorChange])

  return (
    <>
      <Form.Item
        name="source"
        rules={[
          {
            required: true,
            message: t('global:required-field')
          }
        ]}
      >
        <Input.TextArea
          className="text-area"
          ref={saveTextAreaRef}
          autoSize={{ minRows: 4, maxRows: 12 }}
          spellCheck={false}
          onSelect={handleCursorChange}
          onKeyUp={handleCursorChange}
          onClick={handleCursorChange}
          onChange={handleSourceChange}
        />
      </Form.Item>

      <Divider />
      <ContextAwareFunctionInformation addArgument={addArgument} contextInfo={contextInfo} />
    </>
  )
}

export default FormulaSource
